/**
 * Hardened Fields Configuration
 *
 * These fields are automatically included as a locked first step in every NEW form.
 * They ensure a common set of data is captured across all forms with consistent keys.
 *
 * - Admin can see these fields but cannot edit, delete, or reorder them.
 * - Admin CAN add additional fields after the hardened ones in the same step.
 * - Admin CAN rename the step title/description but NOT the step_id.
 * - Existing forms are NOT affected.
 *
 * To add a new hardened field:
 *   1. Add a new object to the HARDENED_FIELDS array below.
 *   2. Include all required properties (key, label, type, required, etc.).
 *   3. Set isHardened: true (this is the marker used by the UI to lock the field).
 *   4. For fields with options (dropdown, radio, gender, etc.), include the options array.
 *   5. To add multilingual labels later, add label_hi, label_ta, etc. properties.
 */

export const HARDENED_FIELDS = [
  {
    key: "full_name",
    label: "Full Name",
    type: "text",
    required: true,
    isFilter: false,
    isVisibleToEmployer: true,
    visibleToEmployerLabel: "Full Name",
    isRequiredInRegionFilter: false,
    isHardened: true,
  },
  {
    key: "date_of_birth",
    label: "Date of Birth",
    type: "date",
    required: true,
    isFilter: false,
    isVisibleToEmployer: false,
    visibleToEmployerLabel: "",
    isRequiredInRegionFilter: false,
    isHardened: true,
  },
  {
    key: "gender",
    label: "Gender",
    type: "gender",
    required: true,
    isFilter: true,
    isVisibleToEmployer: true,
    visibleToEmployerLabel: "Gender",
    isRequiredInRegionFilter: false,
    isHardened: true,
    options: [
      { label: "Male", value: "male" },
      { label: "Female", value: "female" },
      { label: "Other", value: "other" },
    ],
  },
];

export const DEFAULT_HARDENED_STEP = {
  step_id: "personal_info",
  title: "Personal Info",
  description: "",
  isHardened: true,
  fields: HARDENED_FIELDS,
};

/**
 * Returns a deep-cloned copy of the default hardened step.
 * Always use this function when injecting the step to prevent mutation.
 */
export const getHardenedStep = () => {
  return JSON.parse(JSON.stringify(DEFAULT_HARDENED_STEP));
};

/**
 * Builds a hardened step from API-fetched fields.
 * Each field gets isHardened: true marker added.
 * @param {Array} apiFields - Fields returned from GET /hardened-fields API
 * @returns {Object} Step object with hardened fields
 */
export const buildHardenedStepFromApi = (apiFields) => {
  const fields = apiFields.map((f) => ({
    ...f,
    isHardened: true,
  }));
  return {
    step_id: "personal_info",
    title: "Personal Info",
    description: "",
    isHardened: true,
    fields,
  };
};

/**
 * Checks if a field key belongs to the hardened fields set.
 */
export const isHardenedField = (fieldKey) => {
  return HARDENED_FIELDS.some((f) => f.key === fieldKey);
};

/**
 * Checks if a field key belongs to a given set of hardened field keys.
 * Use this when checking against dynamically loaded hardened fields.
 */
export const isHardenedFieldKey = (fieldKey, hardenedKeys) => {
  return hardenedKeys.has(fieldKey);
};

/**
 * Returns all hardened field keys as a Set for fast lookups.
 */
export const getHardenedFieldKeys = () => {
  return new Set(HARDENED_FIELDS.map((f) => f.key));
};
